import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  AlertTriangle,
  ArrowLeft,
  Cloud,
  Database,
  Download,
  Eye,
  FileText,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  UploadCloud,
  User
} from 'lucide-react';
import api from '../../services/api';
import LoaderProgress from '../../components/ui/loader-progress';

const DOC_TYPES = [
  { value: 'estratto_ruolo', label: 'Estratto ruolo' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'rottamazione', label: 'Rottamazione' },
  { value: 'cartella', label: 'Cartella' },
  { value: 'notifica', label: 'Notifica' },
  { value: 'altro', label: 'Altro' }
];

const MODEL_OPTIONS = [
  { value: 'anthropic:claude-haiku-4-5', label: 'Claude Haiku 4.5 (Anthropic)' },
  { value: 'anthropic:claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 pinned (Anthropic)' },
  { value: 'anthropic:claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Anthropic)' },
  { value: 'anthropic:claude-opus-4-7', label: 'Claude Opus 4.7 (Anthropic)' },
  { value: 'google:gemini-3-flash-preview', label: 'Gemini 3 Flash Preview (Google)' },
  { value: 'openai:gpt-4o-mini', label: 'GPT-4o mini (OpenAI)' }
];

const MAX_UPLOAD_MB = 20;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export default function ClaudePosizioneDebitoria() {
  const [view, setView] = useState('list');
  const [cf, setCf] = useState('');
  const [cliente, setCliente] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [lastErrorDebug, setLastErrorDebug] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [analyzeJob, setAnalyzeJob] = useState(null);
  const [clientiList, setClientiList] = useState([]);
  const [clientiSearch, setClientiSearch] = useState('');
  const [loadingClientiList, setLoadingClientiList] = useState(false);

  const [tipoUpload, setTipoUpload] = useState('estratto_ruolo');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dataDocumentoUpload, setDataDocumentoUpload] = useState('');
  const [deleteDocumentoTarget, setDeleteDocumentoTarget] = useState(null);
  const [deleteReportTarget, setDeleteReportTarget] = useState(null);
  const [deletingDocumento, setDeletingDocumento] = useState(false);
  const [deletingReport, setDeletingReport] = useState(false);

  const [extractionModelPreset, setExtractionModelPreset] = useState('anthropic:claude-haiku-4-5');
  const [reportModelPreset, setReportModelPreset] = useState('anthropic:claude-sonnet-4-6');
  const [extractionModelCustom, setExtractionModelCustom] = useState('');
  const [reportModelCustom, setReportModelCustom] = useState('');
  const extractionModel = useMemo(
    () => (extractionModelPreset === '__custom__' ? extractionModelCustom : extractionModelPreset),
    [extractionModelCustom, extractionModelPreset]
  );
  const reportModel = useMemo(
    () => (reportModelPreset === '__custom__' ? reportModelCustom : reportModelPreset),
    [reportModelCustom, reportModelPreset]
  );
  const [analyzing, setAnalyzing] = useState(false);

  const [activeReport, setActiveReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState(0);
  const [outputTab, setOutputTab] = useState(0);
  const [reportPdfPreviewUrl, setReportPdfPreviewUrl] = useState(null);
  const [reportPdfPreviewMimeType, setReportPdfPreviewMimeType] = useState('application/pdf');
  const [loadingReportPdf, setLoadingReportPdf] = useState(false);
  const [cartellePage, setCartellePage] = useState(0);

  const normalizedCf = useMemo(() => String(cf || '').trim().toUpperCase(), [cf]);
  const normalizedClientiSearch = useMemo(() => String(clientiSearch || '').trim(), [clientiSearch]);
  const sleep = useCallback((ms) => new Promise((resolve) => setTimeout(resolve, ms)), []);

  const getClienteLabel = useCallback((c) => {
    if (!c) return '';
    const ragione = String(c.ragione_sociale || '').trim();
    if (ragione) return ragione;
    const nome = String(c.name || '').trim();
    const cognome = String(c.surname || '').trim();
    const joined = `${cognome} ${nome}`.trim();
    return joined || String(c.codice_fiscale || '').trim() || `ID ${c.id}`;
  }, []);

  const renderClienteCell = useCallback(
    (c) => {
      const label = getClienteLabel(c);
      const phone = String(c?.telephone || '').trim();
      const email = String(c?.email || '').trim();
      return (
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                color: '#2563eb',
                backgroundColor: 'rgba(37, 99, 235, 0.12)'
              }}
            >
              <User size={16} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                color: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.12)'
              }}
            >
              <Phone size={16} />
            </Box>
            <Typography variant="body2">{phone || '-'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                color: '#f59e0b',
                backgroundColor: 'rgba(245, 158, 11, 0.12)'
              }}
            >
              <Mail size={16} />
            </Box>
            <Typography variant="body2">{email || '-'}</Typography>
          </Box>
        </Box>
      );
    },
    [getClienteLabel]
  );

  const formatDateIt = useCallback((value) => {
    const s = String(value || '').trim();
    if (!s) return '-';
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      if (Number.isNaN(dt.getTime())) return s;
      return dt.toLocaleDateString('it-IT');
    }
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return s;
    return dt.toLocaleDateString('it-IT');
  }, []);

  const formatDateTimeIt = useCallback((value) => {
    const s = String(value || '').trim();
    if (!s) return '-';
    const dt = new Date(s);
    if (Number.isNaN(dt.getTime())) return s;
    return dt.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const parseMoneyIt = useCallback((value) => {
    const s = String(value || '').trim();
    if (!s) return null;
    const cleaned = s.replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return null;
    const normalized = cleaned.replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }, []);

  const extractFirstMatch = useCallback((text, patterns) => {
    const t = String(text || '');
    for (const p of patterns || []) {
      const m = t.match(p);
      if (m && m[1]) return String(m[1]).trim();
    }
    return null;
  }, []);

  const formatCurrencyIt = useCallback((value) => {
    if (value == null) return null;
    const n = typeof value === 'number' ? value : parseMoneyIt(value);
    if (n == null) return typeof value === 'string' && value.trim() ? value.trim() : null;
    return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  }, [parseMoneyIt]);

  const fetchClientiList = useCallback(async () => {
    setLoadingClientiList(true);
    try {
      const res = await api.get('/claude/clienti', {
        params: normalizedClientiSearch ? { search: normalizedClientiSearch } : undefined
      });
      const data = res.data || {};
      setClientiList(Array.isArray(data.clienti) ? data.clienti : []);
    } catch (error) {
      setClientiList([]);
      toast.error('Errore caricando la lista clienti');
    } finally {
      setLoadingClientiList(false);
    }
  }, [normalizedClientiSearch]);

  const fetchClienteDetails = useCallback(async (id) => {
    const res = await api.get(`/claude/clienti/${id}`);
    const data = res.data || {};
    setCliente(data.cliente || null);
    setDocumenti(Array.isArray(data.documenti) ? data.documenti : []);
    setReports(Array.isArray(data.reports) ? data.reports : []);
  }, []);

  const handleLookup = useCallback(async () => {
    if (!normalizedCf) {
      toast.error('Inserisci un codice fiscale');
      return;
    }
    setLoadingCliente(true);
    try {
      const res = await api.post('/claude/clienti/lookup', { codice_fiscale: normalizedCf });
      const data = res.data || {};
      const c = data.cliente || null;
      setCliente(c);
      setActiveReport(null);
      if (c && c.id) {
        await fetchClienteDetails(c.id);
        await fetchClientiList();
        setView('detail');
      }
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante la ricerca cliente');
    } finally {
      setLoadingCliente(false);
    }
  }, [fetchClienteDetails, fetchClientiList, normalizedCf]);

  const handleSelectClienteFromList = useCallback(
    async (c) => {
      if (!c?.id) return;
      setLoadingCliente(true);
      try {
        setCliente(c);
        setCf(c.codice_fiscale || '');
        setActiveReport(null);
        await fetchClienteDetails(c.id);
        setView('detail');
      } catch (error) {
        toast.error('Errore aprendo il cliente');
      } finally {
        setLoadingCliente(false);
      }
    },
    [fetchClienteDetails]
  );

  const handleNewCliente = useCallback(() => {
    setView('detail');
    setCliente(null);
    setDocumenti([]);
    setReports([]);
    setActiveReport(null);
    setCf('');
  }, []);

  const handleBackToList = useCallback(() => {
    setView('list');
    setCliente(null);
    setDocumenti([]);
    setReports([]);
    setActiveReport(null);
    setCf('');
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!cliente?.id) return;
    setLoadingCliente(true);
    try {
      await fetchClienteDetails(cliente.id);
      await fetchClientiList();
    } catch (error) {
      toast.error('Errore durante l’aggiornamento');
    } finally {
      setLoadingCliente(false);
    }
  }, [cliente?.id, fetchClienteDetails, fetchClientiList]);

  const handleOpenPurgeDialog = useCallback(() => {
    if (!cliente?.id) return;
    setPurgeDialogOpen(true);
  }, [cliente?.id]);

  const handleClosePurgeDialog = useCallback(() => {
    if (purging) return;
    setPurgeDialogOpen(false);
  }, [purging]);

  const handleConfirmPurgeCliente = useCallback(async () => {
    if (!cliente?.id) return;
    setPurging(true);
    try {
      const res = await api.delete(`/claude/clienti/${cliente.id}/purge`);
      const deleted = res?.data?.deleted || null;
      setActiveReport(null);
      setDocumenti([]);
      setReports([]);
      setPurgeDialogOpen(false);
      setCliente(null);
      setCf('');
      setView('list');
      toast.success(
        deleted
          ? `Cancellati: ${deleted.documenti || 0} documenti, ${deleted.reports || 0} report`
          : 'Dati cliente cancellati'
      );
      await fetchClientiList();
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante la cancellazione');
    } finally {
      setPurging(false);
    }
  }, [cliente?.id, fetchClientiList]);

  const handleUpload = useCallback(async () => {
    if (!cliente?.id) {
      toast.error('Seleziona prima un cliente');
      return;
    }
    if (!files || !files.length) {
      toast.error('Seleziona almeno un file');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('tipo', tipoUpload);
      const normalizedDate = String(dataDocumentoUpload || '').trim();
      if (normalizedDate) {
        form.append('data_documento', normalizedDate);
      }
      Array.from(files).forEach((f) => form.append('files', f));
      const uploadRes = await api.post(`/claude/clienti/${cliente.id}/documenti`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documenti caricati');
      const warnings = uploadRes?.data?.warnings;
      if (Array.isArray(warnings) && warnings.length) {
        const filesLabel = warnings
          .map((w) => (w && w.file ? String(w.file) : null))
          .filter(Boolean)
          .slice(0, 4)
          .join(', ');
        toast.warn(filesLabel ? `Caricati con avvisi OCR: ${filesLabel}` : 'Caricati con avvisi OCR');
      }
      setFiles([]);
      setDataDocumentoUpload('');
      await fetchClienteDetails(cliente.id);
      await fetchClientiList();
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  }, [cliente?.id, dataDocumentoUpload, fetchClienteDetails, fetchClientiList, files, tipoUpload]);

  const handleConfirmDeleteDocumento = useCallback(async () => {
    if (!cliente?.id || !deleteDocumentoTarget?.id) return;
    setDeletingDocumento(true);
    try {
      await api.delete(`/claude/clienti/${cliente.id}/documenti/${deleteDocumentoTarget.id}`);
      toast.success('Documento eliminato');
      setDeleteDocumentoTarget(null);
      await fetchClienteDetails(cliente.id);
      await fetchClientiList();
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante la cancellazione del documento');
    } finally {
      setDeletingDocumento(false);
    }
  }, [cliente?.id, deleteDocumentoTarget?.id, fetchClienteDetails, fetchClientiList]);

  const handleConfirmDeleteReport = useCallback(async () => {
    if (!deleteReportTarget?.id) return;
    setDeletingReport(true);
    try {
      await api.delete(`/claude/reports/${deleteReportTarget.id}`);
      toast.success('Report eliminato');
      if (activeReport?.id === deleteReportTarget.id) {
        setActiveReport(null);
        setReportPdfPreviewUrl(null);
      }
      setDeleteReportTarget(null);
      if (cliente?.id) {
        await fetchClienteDetails(cliente.id);
        await fetchClientiList();
      }
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante la cancellazione del report');
    } finally {
      setDeletingReport(false);
    }
  }, [activeReport?.id, cliente?.id, deleteReportTarget?.id, fetchClienteDetails, fetchClientiList]);

  const handleAnalyze = useCallback(async () => {
    if (!cliente?.id) {
      toast.error('Seleziona prima un cliente');
      return;
    }
    if (!extractionModel.trim() || !reportModel.trim()) {
      toast.error('Inserisci i modelli (extractionModel e reportModel)');
      return;
    }
    setAnalyzing(true);
    try {
      const startRes = await api.post(`/claude/clienti/${cliente.id}/analyze-job`, {
        extractionModel: extractionModel.trim(),
        reportModel: reportModel.trim(),
        generateReport: true
      });
      const jobId = startRes?.data?.jobId;
      if (!jobId) {
        throw new Error('JobId non disponibile');
      }
      const payload = { jobId: String(jobId), clienteId: Number(cliente.id) };
      setAnalyzeJob(payload);
      localStorage.setItem('claude_analyze_job_v1', JSON.stringify(payload));
      toast.info('Analisi avviata in background');
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message;
      const dbg = error?.response?.data?.debug || { message: error?.message || null, code: error?.code || null };
      setLastErrorDebug(dbg);
      toast.error(msg || 'Errore durante l’analisi');
      setAnalyzing(false);
    } finally {
      // Lo stato "analyzing" viene chiuso al completamento/fallimento del job
    }
  }, [cliente?.id, extractionModel, reportModel]);

  const handleOpenReport = useCallback(async (reportId) => {
    setRightPanelTab(1);
    setOutputTab(0);
    setReportPdfPreviewUrl(null);
    setLoadingReport(true);
    try {
      const res = await api.get(`/claude/reports/${reportId}`);
      const data = res.data || {};
      setActiveReport(data.report || null);
    } catch (error) {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoadingReport(false);
    }
  }, []);

  const handlePreviewReportPdf = useCallback(async (reportId) => {
    setRightPanelTab(1);
    setOutputTab(3);
    setLoadingReportPdf(true);
    try {
      const res = await api.get(`/claude/reports/${reportId}/download-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setReportPdfPreviewUrl(url);
      setReportPdfPreviewMimeType('application/pdf');
    } catch (error) {
      const msg = error?.response?.data?.message;
      toast.error(msg || 'Errore durante il caricamento del PDF');
    } finally {
      setLoadingReportPdf(false);
    }
  }, []);

  const handlePreviewDocumentoPdf = useCallback(
    async (doc) => {
      if (!cliente?.id || !doc?.id) return;
      setRightPanelTab(1);
      setOutputTab(3);
      setLoadingReportPdf(true);
      try {
        const res = await api.get(`/claude/clienti/${cliente.id}/documenti/${doc.id}/download-pdf`, { responseType: 'blob' });
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        setReportPdfPreviewUrl(url);
        setReportPdfPreviewMimeType(String(doc?.mime_type || res.headers?.['content-type'] || 'application/pdf'));
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(msg || 'Errore durante il caricamento del PDF');
      } finally {
        setLoadingReportPdf(false);
      }
    },
    [cliente?.id]
  );

  const handleDownloadDocumento = useCallback(
    async (doc) => {
      try {
        const res = await api.get(`/claude/clienti/${cliente.id}/documenti/${doc.id}/download`);
        const url = res?.data?.url;
        if (!url) {
          toast.error('URL download non disponibile');
          return;
        }
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.nome_file || `documento_${doc.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(msg || 'Errore durante il download del documento');
      }
    },
    [cliente?.id]
  );

  useEffect(() => {
    if (!reportPdfPreviewUrl) return;
    return () => {
      try {
        window.URL.revokeObjectURL(reportPdfPreviewUrl);
      } catch {}
    };
  }, [reportPdfPreviewUrl]);

  useEffect(() => {
    fetchClientiList().catch(() => {});
    if (cliente?.id) {
      fetchClienteDetails(cliente.id).catch(() => {});
    }
  }, [cliente?.id, fetchClienteDetails, fetchClientiList]);

  useEffect(() => {
    if (analyzeJob) return;
    try {
      const raw = localStorage.getItem('claude_analyze_job_v1');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.jobId && parsed?.clienteId) {
        setAnalyzeJob({ jobId: String(parsed.jobId), clienteId: Number(parsed.clienteId) });
        setAnalyzing(true);
      }
    } catch {}
  }, [analyzeJob]);

  useEffect(() => {
    if (!analyzeJob?.jobId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const statusRes = await api.get(`/claude/jobs/${analyzeJob.jobId}`);
        const status = statusRes?.data || {};
        const state = status.state;

        if (cancelled) return;

        if (state === 'completed') {
          const result = status.result || null;
          setActiveReport(result?.report || null);
          setLastErrorDebug(null);

          if (result?.extraction?.fallback?.to) {
            toast.warn(`Estrrazione eseguita con fallback: ${result.extraction.fallback.to}`);
          } else {
            toast.success('Analisi completata');
          }
          if (result?.reportMeta?.fallback?.to) {
            toast.warn(`Report generato con fallback: ${result.reportMeta.fallback.to}`);
          }
          if (result?.reportMeta?.error) {
            toast.error(result.reportMeta.error);
          }

          await fetchClientiList();
          if (analyzeJob?.clienteId) {
            await fetchClienteDetails(analyzeJob.clienteId);
          }

          localStorage.removeItem('claude_analyze_job_v1');
          setAnalyzeJob(null);
          setAnalyzing(false);
          return;
        }

        if (state === 'failed') {
          const failedReason = status.failedReason || 'Job fallito';
          setLastErrorDebug({ message: failedReason, code: 'JOB_FAILED' });
          toast.error(failedReason);
          localStorage.removeItem('claude_analyze_job_v1');
          setAnalyzeJob(null);
          setAnalyzing(false);
          return;
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Errore durante l’analisi';
        const dbg = error?.response?.data?.debug || { message: error?.message || null, code: error?.code || null };
        setLastErrorDebug(dbg);
        toast.error(msg);
        localStorage.removeItem('claude_analyze_job_v1');
        setAnalyzeJob(null);
        setAnalyzing(false);
      }
    };

    const interval = setInterval(() => {
      tick().catch(() => {});
    }, 2000);

    tick().catch(() => {});

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [analyzeJob?.jobId, analyzeJob?.clienteId, fetchClienteDetails, fetchClientiList]);

  useEffect(() => {
    if (view !== 'list') return;
    const t = setTimeout(() => {
      fetchClientiList().catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [fetchClientiList, normalizedClientiSearch, view]);

  const riepilogo = activeReport?.contenuto_json?.riepilogo || null;
  const contribuente = activeReport?.contenuto_json?.contribuente || null;
  const cartelle = Array.isArray(activeReport?.contenuto_json?.cartelle) ? activeReport.contenuto_json.cartelle : [];

  const mdText = activeReport?.contenuto_md || '';
  const contribuenteFallback = useMemo(
    () =>
      extractFirstMatch(mdText, [
        /Contribuente:\s*([^\n]+)/i,
        /##\s*Contribuente:\s*([^\n]+)/i
      ]),
    [extractFirstMatch, mdText]
  );
  const dataEstrattoFallback = useMemo(
    () =>
      extractFirstMatch(mdText, [
        /Data\s*Estratto:\s*\**\s*([0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4})/i,
        /Data\s*estratto:\s*\**\s*([0-9]{1,2}\/[0-9]{1,2}\/[0-9]{2,4})/i
      ]),
    [extractFirstMatch, mdText]
  );
  const ambitoFallback = useMemo(
    () =>
      extractFirstMatch(mdText, [
        /Ambito\s*ADER:\s*([^\n]+)/i,
        /Ambito:\s*([^\n]+)/i
      ]),
    [extractFirstMatch, mdText]
  );
  const totaleDaPagareFallback = useMemo(() => {
    const raw = extractFirstMatch(mdText, [
      /Totale\s*da\s*Pagare\*{0,2}[^0-9]*([0-9][0-9\.\,]*)/i,
      /Totale\s*da\s*pagare\*{0,2}[^0-9]*([0-9][0-9\.\,]*)/i
    ]);
    return raw ? formatCurrencyIt(raw) : null;
  }, [extractFirstMatch, formatCurrencyIt, mdText]);

  const contribuenteLabel =
    contribuente?.cognome_nome || contribuente?.codice_fiscale || contribuenteFallback || 'DA VERIFICARE';
  const dataEstrattoLabel = riepilogo?.data_estratto || dataEstrattoFallback || 'DA VERIFICARE';
  const ambitoLabel = riepilogo?.ambito_provinciale || contribuente?.ambito_provinciale || ambitoFallback || 'DA VERIFICARE';
  const totaleDaPagareLabel =
    formatCurrencyIt(riepilogo?.totale_da_pagare) || totaleDaPagareFallback || 'DA VERIFICARE';

  const getCartellaImportoDaPagare = useCallback((c) => {
    if (c && typeof c.importo_da_pagare === 'number') return c.importo_da_pagare;
    if (c && c.importi && typeof c.importi.da_pagare === 'number') return c.importi.da_pagare;
    return null;
  }, []);
  const getCartellaNotificaVerificata = useCallback((c) => {
    if (c && typeof c.notifica_verificata === 'boolean') return c.notifica_verificata;
    if (c && c.analisi_notifica && typeof c.analisi_notifica.notifica_verificata === 'boolean') {
      return c.analisi_notifica.notifica_verificata;
    }
    return false;
  }, []);
  const getCartellaNotificaRegolare = useCallback((c) => {
    if (c && typeof c.notifica_regolare === 'boolean') return c.notifica_regolare;
    if (c && c.analisi_notifica && typeof c.analisi_notifica.notifica_regolare === 'boolean') {
      return c.analisi_notifica.notifica_regolare;
    }
    return null;
  }, []);
  const cartelleRowsPerPage = 15;
  const cartellePageSafe = Math.max(0, cartellePage);
  const cartelleSliceStart = cartellePageSafe * cartelleRowsPerPage;
  const cartelleSliceEnd = cartelleSliceStart + cartelleRowsPerPage;
  const cartellePageRows = cartelle.slice(cartelleSliceStart, cartelleSliceEnd);

  useEffect(() => {
    setCartellePage(0);
  }, [activeReport?.id]);

  if (view === 'list') {
    return (
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2
          }}
        >
          <Box sx={{ minWidth: 320 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Simulazione Claude · Posizione Debitoria ADER
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clienti elaborati
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={fetchClientiList} disabled={loadingClientiList}>
              Aggiorna
            </Button>
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={handleNewCliente}>
              Nuovo cliente
            </Button>
          </Box>
        </Box>

        <Card sx={{ p: 2 }}>
          <TextField
            placeholder="Cerca per CF, nome, cognome, ragione sociale…"
            size="small"
            fullWidth
            value={clientiSearch}
            onChange={(e) => setClientiSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchClientiList();
            }}
            sx={{ mb: 1.5 }}
          />

          {loadingClientiList ? (
            <LoaderProgress />
          ) : !clientiList.length ? (
            <Typography variant="body2" color="text.secondary">
              Nessun cliente elaborato.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Codice fiscale</TableCell>
                    <TableCell align="right">Doc</TableCell>
                    <TableCell align="right">Report</TableCell>
                    <TableCell>Creato</TableCell>
                    <TableCell>Modificato</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientiList.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.id}</TableCell>
                      <TableCell>{renderClienteCell(c)}</TableCell>
                      <TableCell>{c.codice_fiscale || '-'}</TableCell>
                      <TableCell align="right">{Number(c.docs_count || 0)}</TableCell>
                      <TableCell align="right">{Number(c.reports_count || 0)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'grid', gap: 0.25 }}>
                          <Typography variant="body2">{formatDateTimeIt(c.created_at)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.created_by_name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'grid', gap: 0.25 }}>
                          <Typography variant="body2">{formatDateTimeIt(c.updated_at)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.updated_by_name || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleSelectClienteFromList(c)}
                          aria-label="Dettaglio"
                          sx={{ color: '#2563eb' }}
                        >
                          <Eye size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 320 }}>
          <IconButton onClick={handleBackToList} aria-label="Indietro">
            <ArrowLeft size={18} />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Dettaglio cliente · Posizione Debitoria ADER
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload documenti → estrazione dati (Haiku) → report finale (Sonnet)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={handleRefresh} disabled={loadingCliente || !cliente?.id}>
            Aggiorna
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Cliente
            </Typography>
            {!cliente ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Codice fiscale"
                  size="small"
                  value={cf}
                  onChange={(e) => setCf(e.target.value)}
                  sx={{ flex: 1, minWidth: 220 }}
                />
                <Button variant="contained" onClick={handleLookup} disabled={loadingCliente}>
                  Apri
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {renderClienteCell(cliente)}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', color: 'text.secondary' }}>
                  <Typography variant="body2">ID: {cliente.id}</Typography>
                  <Typography variant="body2">CF: {cliente.codice_fiscale}</Typography>
                </Box>
                <Divider />
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleOpenPurgeDialog}
                  disabled={purging || loadingCliente || !cliente?.id}
                >
                  Cancella dati cliente (Claude)
                </Button>
              </Box>
            )}
          </Card>

          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Upload documenti
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo documento</InputLabel>
                  <Select value={tipoUpload} label="Tipo documento" onChange={(e) => setTipoUpload(e.target.value)}>
                    {DOC_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Data documento"
                  size="small"
                  type="date"
                  fullWidth
                  value={dataDocumentoUpload}
                  onChange={(e) => setDataDocumentoUpload(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Tooltip title="Seleziona file">
                  <IconButton
                    component="label"
                    aria-label="Seleziona file"
                    sx={{
                      border: '1px solid rgba(145, 158, 171, 0.5)',
                      borderRadius: 2,
                      width: { xs: '100%', md: 44 },
                      height: 44,
                      color: '#2563eb'
                    }}
                  >
                    <UploadCloud size={18} />
                  <input
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,image/webp"
                    multiple
                    hidden
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);
                      const tooBig = selected.filter((f) => f.size > MAX_UPLOAD_BYTES);
                      if (tooBig.length) {
                        toast.error(`File troppo grande (max ${MAX_UPLOAD_MB}MB): ${tooBig.map((f) => f.name).join(', ')}`);
                      }
                      const valid = selected.filter((f) => f.size <= MAX_UPLOAD_BYTES);
                      setFiles(valid);
                      e.target.value = null;
                    }}
                  />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" fullWidth onClick={handleUpload} disabled={uploading || !cliente?.id}>
                  Carica
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  PDF o immagini (JPG/PNG/WEBP) · max {MAX_UPLOAD_MB}MB per file
                </Typography>
              </Grid>
              {files && files.length ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Selezionati: {Array.from(files).map((f) => f.name).join(', ')}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Card>

          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Analisi AI
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Modello estrazione</InputLabel>
                  <Select
                    value={extractionModelPreset}
                    label="Modello estrazione"
                    onChange={(e) => setExtractionModelPreset(e.target.value)}
                  >
                    {MODEL_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                    <MenuItem value="__custom__">Custom…</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {extractionModelPreset === '__custom__' ? (
                <Grid item xs={12}>
                  <TextField
                    label="extractionModel (provider:modelId)"
                    size="small"
                    fullWidth
                    value={extractionModelCustom}
                    onChange={(e) => setExtractionModelCustom(e.target.value)}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Modello report</InputLabel>
                  <Select
                    value={reportModelPreset}
                    label="Modello report"
                    onChange={(e) => setReportModelPreset(e.target.value)}
                  >
                    {MODEL_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                    <MenuItem value="__custom__">Custom…</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {reportModelPreset === '__custom__' ? (
                <Grid item xs={12}>
                  <TextField
                    label="reportModel (provider:modelId)"
                    size="small"
                    fullWidth
                    value={reportModelCustom}
                    onChange={(e) => setReportModelCustom(e.target.value)}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Sparkles size={16} />}
                  fullWidth
                  onClick={handleAnalyze}
                  disabled={analyzing || !cliente?.id}
                >
                  Genera report con Claude
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 1.5, mb: 2 }}>
            <Tabs value={rightPanelTab} onChange={(_, v) => setRightPanelTab(v)} variant="fullWidth">
              <Tab label="Documenti & Report" />
              <Tab label="Output" />
            </Tabs>
          </Card>

          {rightPanelTab === 0 ? (
            <>
              <Card sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Documenti caricati
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {loadingCliente ? (
                  <LoaderProgress />
                ) : !documenti.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessun documento caricato.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Nome file</TableCell>
                          <TableCell>Data documento</TableCell>
                          <TableCell>Caricato</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documenti.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell>
                              <Chip size="small" label={d.tipo} />
                            </TableCell>
                            <TableCell>{d.nome_file}</TableCell>
                            <TableCell>{formatDateIt(d.data_documento || d.created_at)}</TableCell>
                            <TableCell>{formatDateTimeIt(d.created_at)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip title="Apri PDF">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Apri PDF"
                                      onClick={() => handlePreviewDocumentoPdf(d)}
                                      disabled={!d.storage_path}
                                      sx={{ color: '#2563eb' }}
                                    >
                                      <Eye size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Scarica PDF">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Scarica PDF"
                                      onClick={() => handleDownloadDocumento(d)}
                                      disabled={!d.storage_path}
                                      sx={{ color: '#2563eb' }}
                                    >
                                      <Download size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  aria-label="Elimina documento"
                                  onClick={() => setDeleteDocumentoTarget(d)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>

              <Card sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Report
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {!reports.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessun report generato.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Versione</TableCell>
                          <TableCell>Creato</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reports.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{r.id}</TableCell>
                            <TableCell>v{r.versione}</TableCell>
                            <TableCell>{formatDateTimeIt(r.created_at)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Tooltip title="Apri report">
                                  <IconButton
                                    size="small"
                                    aria-label="Apri report"
                                    onClick={() => handleOpenReport(r.id)}
                                    sx={{ color: '#2563eb' }}
                                  >
                                    <FileText size={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Apri PDF">
                                  <IconButton
                                    size="small"
                                    aria-label="Apri PDF"
                                    onClick={() => handlePreviewReportPdf(r.id)}
                                    sx={{ color: '#2563eb' }}
                                  >
                                    <Eye size={18} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  aria-label="Elimina report"
                                  onClick={() => setDeleteReportTarget(r)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </>
          ) : (
            <Card sx={{ p: 2 }}>
              <Tabs value={outputTab} onChange={(_, v) => setOutputTab(v)} variant="fullWidth">
                <Tab label="Riepilogo" />
                <Tab label="Cartelle" />
                <Tab label="Report MD" />
                <Tab label="Anteprima PDF" />
              </Tabs>
              <Divider sx={{ my: 1.5 }} />

              {outputTab === 3 ? (
                loadingReportPdf ? (
                  <LoaderProgress />
                ) : reportPdfPreviewUrl ? (
                  reportPdfPreviewMimeType && reportPdfPreviewMimeType.startsWith('image/') ? (
                    <Box
                      component="img"
                      src={reportPdfPreviewUrl}
                      alt="Anteprima file"
                      sx={{
                        width: '100%',
                        maxHeight: 560,
                        objectFit: 'contain',
                        border: '1px solid rgba(145, 158, 171, 0.24)',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Box
                      component="iframe"
                      src={reportPdfPreviewUrl}
                      title="Anteprima PDF"
                      sx={{
                        width: '100%',
                        height: 560,
                        border: '1px solid rgba(145, 158, 171, 0.24)',
                        borderRadius: 1
                      }}
                    />
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nessun PDF disponibile.
                  </Typography>
                )
              ) : loadingReport ? (
                <LoaderProgress />
              ) : !activeReport ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Nessun report selezionato.
                  </Typography>
                  {lastErrorDebug ? (
                    <Box sx={{ mt: 2 }}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                          Dettagli errore (debug)
                        </Typography>
                        <Box
                          sx={{
                            border: '1px solid rgba(145, 158, 171, 0.24)',
                            borderRadius: 1,
                            p: 2,
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.5,
                            maxHeight: 320,
                            overflow: 'auto'
                          }}
                        >
                          {JSON.stringify(lastErrorDebug, null, 2)}
                        </Box>
                      </Card>
                    </Box>
                  ) : null}
                </>
              ) : outputTab === 0 ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Contribuente:</strong> {contribuenteLabel}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Data estratto:</strong> {dataEstrattoLabel}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Totale da pagare:</strong>{' '}
                        {totaleDaPagareLabel}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Ambito:</strong> {ambitoLabel}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              ) : outputTab === 1 ? (
                !cartelle.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessuna cartella estratta.
                  </Typography>
                ) : (
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Numero</TableCell>
                            <TableCell>Ente</TableCell>
                            <TableCell>Data notifica</TableCell>
                            <TableCell align="right">Da pagare</TableCell>
                            <TableCell>Notifica</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cartellePageRows.map((c, idx) => (
                            <TableRow key={`${c.numero || idx}`}>
                              <TableCell>{c.numero || '-'}</TableCell>
                              <TableCell>{c.ente_creditore || '-'}</TableCell>
                              <TableCell>{formatDateIt(c.data_notifica)}</TableCell>
                              <TableCell align="right">
                              {typeof getCartellaImportoDaPagare(c) === 'number' ? getCartellaImportoDaPagare(c) : '-'}
                              </TableCell>
                              <TableCell>
                                {getCartellaNotificaVerificata(c) ? (
                                  getCartellaNotificaRegolare(c) === true ? (
                                    <Chip size="small" color="success" label="Regolare" />
                                  ) : getCartellaNotificaRegolare(c) === false ? (
                                    <Chip size="small" color="error" label="Non regolare" />
                                  ) : (
                                    <Chip size="small" variant="outlined" label="Verificata" />
                                  )
                                ) : (
                                  <Chip size="small" variant="outlined" label="Non verificata" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={cartelle.length}
                      page={cartellePageSafe}
                      onPageChange={(_, newPage) => setCartellePage(newPage)}
                      rowsPerPage={cartelleRowsPerPage}
                      rowsPerPageOptions={[cartelleRowsPerPage]}
                      labelRowsPerPage="Righe"
                    />
                  </Box>
                )
              ) : (
                <Box
                  sx={{
                    border: '1px solid rgba(145, 158, 171, 0.24)',
                    borderRadius: 1,
                    p: 2,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5,
                    maxHeight: 520,
                    overflow: 'auto'
                  }}
                >
                  {activeReport.contenuto_md || 'Nessun markdown disponibile.'}
                </Box>
              )}
            </Card>
          )}
        </Grid>
      </Grid>

      <Dialog open={purgeDialogOpen} onClose={handleClosePurgeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'rgba(239, 68, 68, 0.12)'
            }}
          >
            <AlertTriangle size={20} color="#ef4444" />
          </Box>
          <Box sx={{ display: 'grid', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 800 }}>Conferma cancellazione</Typography>
            <Typography variant="body2" color="text.secondary">
              Operazione irreversibile
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'grid', gap: 0.25 }}>
              <Typography variant="body2">
                Stai per cancellare tutti i documenti e report del cliente <strong>{getClienteLabel(cliente)}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID {cliente?.id} · CF {cliente?.codice_fiscale || '-'}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(59, 130, 246, 0.12)'
                  }}
                >
                  <Cloud size={18} color="#3b82f6" />
                </Box>
                <Typography variant="body2">Elimina i file su Wasabi (claude-documenti/{cliente?.id})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(16, 185, 129, 0.12)'
                  }}
                >
                  <Database size={18} color="#10b981" />
                </Box>
                <Typography variant="body2">Elimina le righe dalle tabelle claude_*</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.25,
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: 'rgba(107, 114, 128, 0.12)'
                  }}
                >
                  <User size={18} color="#6b7280" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Non elimina il cliente dal DB remoto (customer)
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5 }}>
          <Button variant="outlined" onClick={handleClosePurgeDialog} disabled={purging}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={handleConfirmPurgeCliente}
            disabled={purging}
          >
            {purging ? 'Eliminazione…' : 'Elimina definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteDocumentoTarget)}
        onClose={() => {
          if (deletingDocumento) return;
          setDeleteDocumentoTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'rgba(239, 68, 68, 0.12)'
            }}
          >
            <AlertTriangle size={20} color="#ef4444" />
          </Box>
          <Box sx={{ display: 'grid', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 800 }}>Elimina documento</Typography>
            <Typography variant="body2" color="text.secondary">
              Operazione irreversibile
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2">
            Vuoi eliminare il documento <strong>{deleteDocumentoTarget?.nome_file || '-'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5 }}>
          <Button variant="outlined" onClick={() => setDeleteDocumentoTarget(null)} disabled={deletingDocumento}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={handleConfirmDeleteDocumento}
            disabled={deletingDocumento}
          >
            {deletingDocumento ? 'Eliminazione…' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteReportTarget)}
        onClose={() => {
          if (deletingReport) return;
          setDeleteReportTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'rgba(239, 68, 68, 0.12)'
            }}
          >
            <AlertTriangle size={20} color="#ef4444" />
          </Box>
          <Box sx={{ display: 'grid', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 800 }}>Elimina report</Typography>
            <Typography variant="body2" color="text.secondary">
              Operazione irreversibile
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2">
            Vuoi eliminare il report <strong>ID {deleteReportTarget?.id}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1.5 }}>
          <Button variant="outlined" onClick={() => setDeleteReportTarget(null)} disabled={deletingReport}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={handleConfirmDeleteReport}
            disabled={deletingReport}
          >
            {deletingReport ? 'Eliminazione…' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
